import React, { useState, useEffect, useRef } from 'react';
import { FaUpload, FaFile } from 'react-icons/fa';
import { lerDocumento, lerDocumentoVeiculo } from '../others/ocrDocumento';
import { useToast } from './ToastContext';
import {
  arquivoParaAnexar as arquivoParaAnexarImportacao, excedeLimiteDeAnexo, TAMANHO_MAXIMO_ANEXO_MB,
} from '../others/importacao';
import { formatarPlaca } from '../others/leituraVeiculo';

const TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'application/pdf'];
// Limite só para a leitura, que é feita no navegador. Guardar o arquivo no cadastro tem o limite menor do servidor
// (TAMANHO_MAXIMO_ANEXO_MB): acima dele os dados são aproveitados, mas o arquivo não é guardado.
const TAMANHO_MAXIMO_LEITURA_MB = 10;
// O que muda entre importar os dados de uma pessoa (CNH, RG) e de um veículo (CRLV, ATPV)
const TIPOS = {
  pessoa: {
    rotuloBotao: 'Importar documento (CNH, RG)',
    rotuloGuardar: 'Guardar o arquivo nos documentos do cliente',
    dica: 'Foto (JPG/PNG) ou PDF do documento. Os dados são lidos no seu computador.',
    campos: ['nome', 'cpfCnpj', 'rg'],
    rotulos: { nome: 'nome', cpfCnpj: 'CPF', rg: 'RG' },
    ler: async (arquivo, progresso) => ({ dados: await lerDocumento(arquivo, progresso), duvidas: [] }),
  },
  veiculo: {
    rotuloBotao: 'Importar CRLV / ATPV',
    rotuloGuardar: 'Guardar o arquivo nos documentos do veículo',
    dica: 'PDF do CRLV-e ou foto (JPG/PNG) do documento. Os dados são lidos no seu computador.',
    campos: ['placa', 'renavam', 'chassi', 'marcaModelo', 'cor', 'ano'],
    // Lidos quando o documento os traz, mas nunca cobrados: não é todo CRLV que os imprime, e o cadastro não os exige.
    // Só chegam às telas que pedem o campo em "campos" (o cadastro do veículo, em Clientes).
    opcionais: ['motor'],
    rotulos: { placa: 'placa', renavam: 'RENAVAM', chassi: 'chassi', marcaModelo: 'marca/modelo', cor: 'cor', ano: 'ano', motor: 'nº do motor' },
    ler: (arquivo, progresso) => lerDocumentoVeiculo(arquivo, progresso),
  },
};

// Reexportado para as telas (a regra fica em others/importacao.js, junto com resolverAnexo)
export const arquivoParaAnexar = arquivoParaAnexarImportacao;

/**
 * Botão "Importar documento (CNH, RG)": lê o documento (JPG, PNG ou PDF) no navegador, gratuito e local,
 * e devolve nome, CPF e RG para a tela preencher os campos. A tela sempre deve deixar o usuário conferir.
 *
 * Componente controlado: o arquivo importado fica no estado da tela, junto da pessoa a que pertence.
 * - importacao: { arquivo, guardar } ou null.
 * - onImportacaoChange(novaImportacao | null): chamado ao importar, marcar/desmarcar "Guardar" ou remover.
 * - tipo: 'pessoa' (CNH, RG: nome, CPF e RG) ou 'veiculo' (CRLV, ATPV: placa, RENAVAM, chassi, nº do motor, marca/modelo, cor e ano).
 * - onDadosLidos(dados, duvidas): chamado com o que foi encontrado; duvidas lista campos que não passaram na validação.
 * - campos: quais campos a tela usa (padrão: todos do tipo). Telas sem campo de RG passam ['nome', 'cpfCnpj'].
 * - semGuardar: só lê os dados, sem a opção de guardar o arquivo (ex.: veículo de origem de um recibo).
 * Ao salvar a pessoa, a tela usa arquivoParaAnexar(importacao) e limpa a importação.
 */
function ImportarDocumento({ tipo = 'pessoa', importacao, onImportacaoChange, onDadosLidos, campos, disabled = false, rotuloGuardar, semGuardar = false }) {
  const config = TIPOS[tipo];
  const camposUsados = campos || config.campos;
  const rotuloDaCaixa = rotuloGuardar || config.rotuloGuardar;
  const { addToast } = useToast();
  const [lendo, setLendo] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [etapa, setEtapa] = useState('');
  const ocultarRef = useRef(null);
  const arquivo = importacao ? importacao.arquivo : null;
  const guardar = importacao ? importacao.guardar : true;

  useEffect(() => () => clearTimeout(ocultarRef.current), []);

  const importar = async (e) => {
    const escolhido = e.target.files[0];
    e.target.value = ''; // permite escolher o mesmo arquivo de novo
    if (!escolhido) return;

    if (!TIPOS_ACEITOS.includes(escolhido.type)) {
      addToast('error', 'Formato não suportado. Envie uma imagem JPG/PNG ou um PDF.');
      return;
    }
    if (escolhido.size > TAMANHO_MAXIMO_LEITURA_MB * 1024 * 1024) {
      addToast('error', `Arquivo muito grande. O limite é ${TAMANHO_MAXIMO_LEITURA_MB} MB.`);
      return;
    }

    clearTimeout(ocultarRef.current);
    setLendo(true);
    setProgresso(0);
    setEtapa('Iniciando...');
    let concluiu = false;
    try {
      const { dados: lidoNoDocumento, duvidas } = await config.ler(escolhido, (percentual, mensagem) => {
        setProgresso(percentual);
        setEtapa(mensagem);
      });
      if (lidoNoDocumento.documentoDeVeiculo) {
        addToast('warning', 'Este parece ser um documento de veículo (CRLV/ATPV). Para importá-lo, use "Importar CRLV / ATPV" no cadastro do veículo.');
        concluiu = true;
        return;
      }
      const dados = Object.fromEntries(Object.entries(lidoNoDocumento).filter(([campo]) => camposUsados.includes(campo)));
      if (dados.placa) dados.placa = formatarPlaca(dados.placa); // mesmo padrão da base: AAA-0A00
      const lidos = Object.keys(dados).map(campo => config.rotulos[campo]);

      if (lidos.length === 0) {
        addToast('warning', 'Não consegui ler dados neste documento. Tente uma imagem mais nítida ou preencha manualmente.');
      } else {
        onDadosLidos(dados, duvidas);
        const opcionais = config.opcionais || [];
        const faltando = camposUsados.filter(campo => !opcionais.includes(campo))
          .map(campo => config.rotulos[campo]).filter(rotulo => !lidos.includes(rotulo));
        const suspeitos = duvidas.filter(campo => camposUsados.includes(campo)).map(campo => config.rotulos[campo]);
        addToast(
          faltando.length || suspeitos.length ? 'warning' : 'success',
          `Documento lido: ${lidos.join(', ')} preenchido(s).${faltando.length ? ` Não encontrei: ${faltando.join(', ')}.` : ''}${suspeitos.length ? ` Confira com atenção: ${suspeitos.join(', ')} (não passou na validação).` : ''} Confira os dados antes de salvar.`
        );
      }
      // referencia: o CPF (pessoa) ou a placa (veículo) do documento, para só anexá-lo ao cadastro que for dele
      onImportacaoChange({ arquivo: escolhido, guardar, referencia: tipo === 'veiculo' ? lidoNoDocumento.placa : lidoNoDocumento.cpfCnpj });
      concluiu = true;
    } catch (error) {
      console.error('Erro ao ler documento:', error);
      addToast('error', 'Não foi possível ler o documento. Preencha os dados manualmente.');
    } finally {
      setLendo(false);
      // Leituras rápidas (JPG) terminam em ~2 s: mantém a barra em 100% por um instante para dar para ver
      ocultarRef.current = setTimeout(() => setEtapa(''), concluiu ? 1500 : 0);
    }
  };

  const alternarGuardar = (valor) => onImportacaoChange({ ...importacao, guardar: valor });
  const remover = () => onImportacaoChange(null);

  const bloqueado = lendo || disabled;

  return (
    <div style={estilos.container}>
      <label style={{ ...estilos.botao, ...(bloqueado ? estilos.botaoDesabilitado : {}) }}>
        <FaUpload /> {lendo ? `Lendo documento... ${progresso}%` : config.rotuloBotao}
        <input
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          onChange={importar}
          disabled={bloqueado}
          style={{ display: 'none' }}
        />
      </label>

      {arquivo ? (
        <span style={estilos.dica}>
          <FaFile /> {arquivo.name}
          {semGuardar ? null : excedeLimiteDeAnexo(arquivo) ? (
            <span style={estilos.aviso}>
              Acima de {TAMANHO_MAXIMO_ANEXO_MB} MB: os dados foram lidos, mas o arquivo não será guardado.
            </span>
          ) : (
            <label style={estilos.guardar}>
              <input type="checkbox" checked={guardar} onChange={(e) => alternarGuardar(e.target.checked)} /> {rotuloDaCaixa}
            </label>
          )}
          <button type="button" onClick={remover} style={estilos.link}>Remover</button>
        </span>
      ) : (
        <span style={estilos.dica}>{config.dica}</span>
      )}

      {etapa && (
        <div style={estilos.progresso} role="progressbar" aria-valuenow={progresso} aria-valuemin={0} aria-valuemax={100}>
          <div style={estilos.trilho}>
            <div style={{ ...estilos.barra, width: `${progresso}%` }} />
          </div>
          <span style={estilos.dica}>{etapa} {progresso}%</span>
        </div>
      )}
    </div>
  );
}

const estilos = {
  container: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' },
  botao: {
    backgroundColor: '#6f42c1', color: '#fff', padding: '8px 16px', borderRadius: '4px', fontSize: '14px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px',
  },
  botaoDesabilitado: { backgroundColor: '#b8c2cc', cursor: 'not-allowed' },
  dica: { fontSize: '12px', color: '#6c757d', display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' },
  guardar: { display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#333' },
  aviso: { color: '#b35c00' },
  link: { background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: 'inherit' },
  progresso: { flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '4px' },
  trilho: { height: '10px', borderRadius: '5px', backgroundColor: '#e3e6ea', overflow: 'hidden' },
  barra: { height: '100%', borderRadius: '5px', backgroundColor: '#6f42c1', transition: 'width 0.3s ease' },
};

export default ImportarDocumento;
